import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

// Tipos para o sistema de roles
export type AppRole = 'admin_municipal' | 'gestor_ouvidoria' | 'presidente_ouvidoria';

export interface UserRole {
  id: string;
  role: AppRole;
  municipio_id: string | null;
  ouvidoria_id: string | null;
}

export interface Profile {
  id: string;
  nome: string;
  email: string;
  telefone: string | null;
  cargo: string | null;
  municipio_id: string | null;
  ouvidoria_id: string | null;
}

export interface Municipio {
  id: string;
  nome: string;
  uf: string;
  codigo_ibge: string;
  cnpj: string;
  populacao: number;
  porte: 'pequena' | 'media' | 'grande' | 'capital';
  plano_ativo: boolean;
  stripe_product_id: string | null;
  subscription_end: string | null;
}

export interface SubscriptionStatus {
  subscribed: boolean;
  productId: string | null;
  subscriptionEnd: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: UserRole[];
  municipio: Municipio | null;
  subscription: SubscriptionStatus | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  
  // Auth actions
  signUp: (email: string, password: string, metadata?: Record<string, any>) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  
  // Role helpers
  hasRole: (role: AppRole) => boolean;
  isAdminMunicipal: () => boolean;
  isGestorOuvidoria: () => boolean;
  isPresidenteOuvidoria: () => boolean;
  
  // Subscription
  checkSubscription: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [municipio, setMunicipio] = useState<Municipio | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch user profile and roles
  const fetchUserData = async (userId: string) => {
    try {
      // Fetch profile
      const { data: profileData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileData) {
        setProfile(profileData as Profile);

        // Fetch municipio if linked
        if (profileData.municipio_id) {
          const { data: municipioData } = await supabase
            .from('municipios')
            .select('*')
            .eq('id', profileData.municipio_id)
            .single();

          if (municipioData) {
            setMunicipio(municipioData as Municipio);
          }
        }
      }

      // Fetch roles
      const { data: rolesData } = await supabase
        .from('user_roles')
        .select('*')
        .eq('user_id', userId);

      if (rolesData) {
        setRoles(rolesData as UserRole[]);
      }
    } catch (error) {
    }
  };

  // Check subscription status
  const checkSubscription = async () => {
    if (!session) return;

    try {
      const { data, error } = await supabase.functions.invoke('check-subscription');
      
      if (error) {
        return;
      }

      setSubscription({
        subscribed: data.subscribed,
        productId: data.product_id,
        subscriptionEnd: data.subscription_end,
      });
    } catch (error) {
    }
  };

  // Refresh profile data
  const refreshProfile = async () => {
    if (user) {
      await fetchUserData(user.id);
    }
  };

  // Initialize auth state
  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        // Defer data fetching with setTimeout to avoid deadlock
        if (session?.user) {
          setTimeout(() => {
            fetchUserData(session.user.id);
            checkSubscription();
          }, 0);
        } else {
          setProfile(null);
          setRoles([]);
          setMunicipio(null);
          setSubscription(null);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchUserData(session.user.id);
        checkSubscription();
      }
      
      setIsLoading(false);
    });

    return () => authSubscription.unsubscribe();
  }, []);

  // Periodically check subscription (every 60 seconds)
  useEffect(() => {
    if (!session) return;

    const interval = setInterval(() => {
      checkSubscription();
    }, 60000);

    return () => clearInterval(interval);
  }, [session]);

  // Auth actions
  const signUp = async (email: string, password: string, metadata?: Record<string, any>) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: metadata,
      },
    });

    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setRoles([]);
    setMunicipio(null);
    setSubscription(null);
  };

  // Role helpers
  const hasRole = (role: AppRole): boolean => {
    return roles.some(r => r.role === role);
  };

  const isAdminMunicipal = () => hasRole('admin_municipal');
  const isGestorOuvidoria = () => hasRole('gestor_ouvidoria');
  const isPresidenteOuvidoria = () => hasRole('presidente_ouvidoria');

  const value: AuthContextType = {
    user,
    session,
    profile,
    roles,
    municipio,
    subscription,
    isLoading,
    isAuthenticated: !!user,
    signUp,
    signIn,
    signOut,
    hasRole,
    isAdminMunicipal,
    isGestorOuvidoria,
    isPresidenteOuvidoria,
    checkSubscription,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
